import React from 'react';

import { SxProps, Theme } from '@mui/material';
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from 'notistack';

import { ICustomAlertAction } from '@/shared/ui/components/alerts/CustomAlert';

export interface ICustomStylesNotistack {
  className?: string;
  icon?: React.ReactNode;
  severity?: 'success' | 'error' | 'warning' | 'info';
  /** Точечные стили плашки поверх общих — вариант в месте вызова */
  alertSx?: SxProps<Theme>;
}

/**
 * Хук для вызова кастомных уведомлений notistack
 */
export const useCustomAlerts = () => {
  /**
   * Ф-ия вызывает уведомление
   * @param text - тест уведомления
   * @param styles - кастомные стили
   * @param autoHideDuration - время отображения уведомления в мс,
   * по умолчанию 5000, если null, то не закрывается автоматически
   * @param options - действие уведомления и опции notistack, не входящие
   * в общий контракт стилей
   * @param options.action - действие уведомления (например «Вернуть» у снятой
   * закладки): рисуется слева от крестика и закрывает уведомление после нажатия
   * @param options.key - ключ дедупликации уведомлений по одной и той же сущности
   * @param options.preventDuplicate - не показывать уведомление повторно, пока
   * с тем же `key` уже открыто
   */
  const showCustomAlert = (
    text: string,
    styles?: ICustomStylesNotistack,
    autoHideDuration?: number | null,
    options?: {
      action?: ICustomAlertAction;
      key?: string;
      preventDuplicate?: boolean;
    }
  ) => {
    enqueueSnackbar(text, {
      autoHideDuration: autoHideDuration,
      variant: 'customAlert',
      key: options?.key,
      preventDuplicate: options?.preventDuplicate,
      /**
       * ф-ия закрытия notistack
       * @param id - id
       */
      onClose: id => closeSnackbar(id as unknown as SnackbarKey),
      SnackbarProps: {
        style: {
          display: 'flex',
          minHeight: '48px',
          whiteSpace: 'break-spaces',
          maxWidth: 'fit-content'
        }
      },
      ...({
        icon: styles?.icon,
        className: styles?.className,
        severity: styles?.severity,
        alertSx: styles?.alertSx,
        undoAction: options?.action
      } as Record<string, unknown>)
    });
  };

  return { showCustomAlert };
};
